import { FormValidator } from '@app/calculators/effort-planner-form/form-validation/formValidator';
import { Validateable } from '@app/calculators/effort-planner-form/form-validation/validateable';
import { BehaviorSubject, Observable } from 'rxjs';
import { AddressModel, AddressModelData, AddressModelDTO } from './addressModel';

export interface BillingContactDTO extends AddressModelDTO {
  billingOrgName: string;
  comment: string;
  comment2: string;
  department: string;
  project: string;
  reference: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}
export interface BillingContactModelData {
  billingOrgName: string | undefined;
  department: string | undefined;
  email: string | undefined;
  firstName: string | undefined;
  lastName: string | undefined;
}

export type BillingContactModelValidationField = 'billingOrgName' | 'email' | 'firstName' | 'lastName';

export class BillingContactModel extends AddressModel implements Validateable {
  private billingOrgNameSubject$: BehaviorSubject<string | undefined>;
  private emailSubject$: BehaviorSubject<string | undefined>;
  private firstNameSubject$: BehaviorSubject<string | undefined>;
  private lastNameSubject$: BehaviorSubject<string | undefined>;
  private departmentSubject$: BehaviorSubject<string | undefined>;

  readonly billingOrgName$: Observable<string | undefined>;
  readonly email$: Observable<string | undefined>;
  readonly firstName$: Observable<string | undefined>;
  readonly lastName$: Observable<string | undefined>;
  readonly department$: Observable<string | undefined>;
  readonly formValidator = new FormValidator<BillingContactModelValidationField>();

  constructor(data: BillingContactModelData | undefined, addressData: AddressModelData | undefined) {
    super(addressData);

    this.billingOrgNameSubject$ = new BehaviorSubject(data?.billingOrgName);
    this.emailSubject$ = new BehaviorSubject(data?.email);
    this.firstNameSubject$ = new BehaviorSubject(data?.firstName);
    this.lastNameSubject$ = new BehaviorSubject(data?.lastName);
    this.departmentSubject$ = new BehaviorSubject(data?.department);
    this.billingOrgName$ = this.billingOrgNameSubject$.asObservable();
    this.email$ = this.emailSubject$.asObservable();
    this.firstName$ = this.firstNameSubject$.asObservable();
    this.lastName$ = this.lastNameSubject$.asObservable();
    this.department$ = this.departmentSubject$.asObservable();
  }

  get billingOrgName() {
    return this.billingOrgNameSubject$.value;
  }

  set billingOrgName(value: string | undefined) {
    this.billingOrgNameSubject$.next(value);
  }

  get email() {
    return this.emailSubject$.value;
  }

  set email(value: string | undefined) {
    this.emailSubject$.next(value);
  }

  get firstName() {
    return this.firstNameSubject$.value;
  }

  set firstName(value: string | undefined) {
    this.firstNameSubject$.next(value);
  }

  get lastName() {
    return this.lastNameSubject$.value;
  }

  set lastName(value: string | undefined) {
    this.lastNameSubject$.next(value);
  }

  get department() {
    return this.departmentSubject$.value;
  }

  set department(value: string | undefined) {
    this.departmentSubject$.next(value);
  }

  override resetValidations(): void {
    super.resetValidations();
    this.formValidator.reset();
  }

  override validate(): void {
    super.validate();

    this.formValidator.validate(
      'billingOrgName',
      'settings.messages.validate.orgname.required',
      (this.billingOrgName?.length ?? 0) > 0,
    );

    this.formValidator.validate(
      'billingOrgName',
      'settings.messages.validate.orgname.maxlength',
      (this.billingOrgName?.length ?? 0) < 50,
    );

    this.formValidator.validate(
      'billingOrgName',
      'settings.messages.validate.orgname.minlength',
      (this.billingOrgName?.length ?? 0) >= 1,
    );

    this.formValidator.validate('email', 'global.messages.validate.email.required', (this.email?.length ?? 0) > 0);

    this.formValidator.validate(
      'email',
      'global.messages.validate.email.invalid',
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email || ''),
    );

    this.formValidator.validate(
      'firstName',
      'register.form.billingContact.firstname.required',
      (this.firstName?.length ?? 0) > 0,
    );

    this.formValidator.validate(
      'firstName',
      'register.form.billingContact.firstname.minlength',
      (this.firstName?.length ?? 0) >= 1,
    );

    this.formValidator.validate(
      'firstName',
      'register.form.billingContact.firstname.maxlength',
      (this.firstName?.length ?? 0) < 50,
    );

    this.formValidator.validate(
      'lastName',
      'register.form.billingContact.lastname.required',
      (this.lastName?.length ?? 0) > 0,
    );

    this.formValidator.validate(
      'lastName',
      'register.form.billingContact.lastname.minlength',
      (this.lastName?.length ?? 0) >= 1,
    );

    this.formValidator.validate(
      'lastName',
      'register.form.billingContact.lastname.maxlength',
      (this.lastName?.length ?? 0) < 50,
    );
  }

  override isValid(): boolean {
    return this.formValidator.isValid && super.isValid();
  }

  toDto(): BillingContactDTO | undefined {
    if (this.billingOrgName === undefined) {
      return undefined;
    }
    if (this.email === undefined) {
      return undefined;
    }
    if (this.firstName === undefined) {
      return undefined;
    }
    if (this.lastName === undefined) {
      return undefined;
    }
    if (this.street === undefined) {
      return undefined;
    }
    if (this.zipCode === undefined) {
      return undefined;
    }
    if (this.place === undefined) {
      return undefined;
    }
    if (this.department === undefined) {
      return undefined;
    }

    return {
      billingOrgName: this.billingOrgName,
      comment: '',
      comment2: '',
      department: this.department,
      email: this.email,
      firstName: this.firstName,
      lastName: this.lastName,
      phone: '',
      place: this.place,
      project: '',
      reference: '',
      street: this.street,
      zipCode: this.zipCode,
    };
  }
}
